/**
 * Nullable-string helpers.
 *
 * These exist because `strict-boolean-expressions` and `prefer-nullish-coalescing` (both errors —
 * see `eslint.config.js`) forbid the shorthand these replace, and rightly: `if (name)` and
 * `name || email` treat a legitimate `''` the same as an absent value. Most of the API's string
 * fields are nullable, and several of them mean different things when blank versus missing.
 *
 * The point is not to appease the linter — a mechanical `=== true` or `??` swap would do that. It
 * is that "has text", "first one with text" and "contains, absent means no match" are the three
 * things the call sites actually meant, and naming them says so once instead of at every site.
 *
 * All three treat `''` exactly as the `||` they replaced did — as *not* a value — so behavior is
 * unchanged. Note they do **not** trim: a string of spaces counts as text, matching the old
 * shorthand rather than quietly fixing it. `trimToNull` below is the one that does trim, and it is
 * about what goes on the wire rather than about what the user typed.
 */

/** True when a nullable string carries actual text — what `if (value)` meant for a string. */
export function hasText(value: string | null | undefined): value is string {
  return value !== null && value !== undefined && value !== '';
}

/**
 * The first argument carrying text, or `null` when none do — what a `a || b || c` fallback chain
 * meant. Returns `null` rather than `''` so the caller picks its own last resort, which is usually
 * a translated string rather than a blank.
 */
export function firstNonBlank(...values: (string | null | undefined)[]): string | null {
  for (const value of values) {
    if (hasText(value)) return value;
  }
  return null;
}

/**
 * Case-insensitive substring test where an absent value is simply no match.
 *
 * Both sides are lower-cased here rather than trusting the caller: every current call site already
 * lower-cases its needle, and a future one that forgets would otherwise fail to match with no
 * visible cause.
 */
export function containsText(value: string | null | undefined, needle: string): boolean {
  return hasText(value) && value.toLowerCase().includes(needle.toLowerCase());
}

/**
 * `value` with surrounding whitespace removed, or `null` when nothing is left.
 *
 * The trimming one, and the only one whose subject is the payload rather than the input: the API
 * rejects a device serial number that is blank *or* whitespace, so a caller has to test the string
 * it is about to send instead of the one it holds. `hasText('  ')` is true and correctly so —
 * `trimToNull('  ')` is `null`, which is the question a write needs answered.
 */
export function trimToNull(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
